import { createForm, useCreateFormStore } from '@components/form';
import {
  ICON_CHOOSER_ICONS,
  ITEM_CHOOSER_COLORS,
  IconChooserValue,
  IconName,
} from '@components/icon-chooser/icon-chooser';
import { IconWorkspace } from '@components/icons/workspace';
import { RelativeSpinner } from '@components/loading-spinner';
import { SelectOption } from '@components/selectmulti';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUsers } from 'api/users/hooks';
import { useCreateWorkspace, useUpdateWorkspace, useWorkspace } from 'api/workspaces/hooks';

export const useWorkspaceFormSidebar = () => {
  return useSidebar(WorkspaceFormContainer, { variant: 'CONTENT_HEAVY' });
};

type WorkspaceData = {
  title: string;
  description: string;
  icon: IconChooserValue;
  isPrivate: boolean;
  members: SelectOption[];
};

const Form = createForm<WorkspaceData>();

const getRandomIcon = () => {
  return {
    icon: Object.keys(ICON_CHOOSER_ICONS)[
      Math.floor(Math.random() * Object.keys(ICON_CHOOSER_ICONS).length)
    ] as IconName,
    color: ITEM_CHOOSER_COLORS[Math.floor(Math.random() * ITEM_CHOOSER_COLORS.length)],
  };
};

const WorkspaceFormContainer = ({ workspaceId = undefined }: { workspaceId?: string }) => {
  const { t } = useScopedTranslate(locale);
  const userQuery = useUsers('?ordering=name&ignore_invited=true');
  const { data } = useWorkspace(workspaceId ?? '');
  if (!userQuery.data) {
    return <RelativeSpinner />;
  }
  if (workspaceId && !data) {
    return <RelativeSpinner />;
  }
  const formData =
    workspaceId && data
      ? {
          title: data.title ?? '',
          description: data.description ?? '',
          members: data.members,
          isPrivate: data.isPrivate,
          icon: { icon: data.icon, color: data.color },
        }
      : {
          title: '',
          description: '',
          members: [],
          isPrivate: false,
          icon: getRandomIcon(),
        };
  const memberOptions: SelectOption[] = userQuery.data
    .filter((user) => !user.isMe)
    .map((user) => ({
      label: user.name,
      value: user.shortId,
    }));

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t(workspaceId ? 'editWorkspace' : 'createWorkspace')}
        icon={IconWorkspace}
        iconColor="elementBlue100"
      />
      <Sidebar.Content>
        <WorkspaceForm workspaceId={workspaceId} memberOptions={memberOptions} {...formData} />
      </Sidebar.Content>
    </Sidebar>
  );
};

type WorkspaceFormProps = Omit<WorkspaceData, 'members'> & {
  workspaceId?: string;
  memberOptions: SelectOption[];
  members: string[];
};

const WorkspaceForm = ({
  workspaceId,
  title,
  description,
  memberOptions,
  members,
  isPrivate,
  icon,
}: WorkspaceFormProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isPrivateValue = formStore.useFieldValue('isPrivate');
  const isDirty = formStore.useIsDirty();
  const createMutation = useCreateWorkspace();
  const updateMutation = useUpdateWorkspace();
  const handleSubmit = async ({ icon: iconData, ...rest }: WorkspaceData) => {
    const { icon, color } = iconData;
    if (workspaceId) {
      await updateMutation.mutateAsync({
        shortId: workspaceId,
        data: {
          ...rest,
          icon,
          color,
          members: rest.members?.map((member) => member.value) ?? [],
        },
      });
    } else {
      await createMutation.mutateAsync({
        ...rest,
        icon,
        color,
        members: rest.members?.map((member) => member.value) ?? [],
        isPrivate: rest.isPrivate ?? false,
      });
    }
    hideSidebar();
  };
  const selectedMembers = memberOptions.filter((member) => members.includes(member.value));
  return (
    <Form store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <Sidebar.SplitContainer label={t('details')}>
          <Stack space={4}>
            <Text text={t('detailsDescription')} font="regular14" />
            <Stack space={2} orientation="horizontal" align="center">
              <Form.IconChooser label={t('iconChooser')} id="icon" defaultValue={icon} />
              <Text text={t('chooseIconAndColor')} font="semibold14" />
            </Stack>
            <Form.TextField
              label={t('title')}
              validators={['required']}
              defaultValue={title}
              id="title"
              placeholder={t('titlePlaceholder')}
            />
            <Form.TextField
              label={t('description')}
              id="description"
              defaultValue={description}
              placeholder={t('descriptionPlaceholder')}
            />
          </Stack>
        </Sidebar.SplitContainer>
        <Sidebar.Separator />
        <Sidebar.SplitContainer label={t('privacy')}>
          <Stack space={4}>
            <Text text={t('privateDescription')} font="regular14" />
            <Form.Switch
              defaultValue={isPrivate}
              labelSide="left"
              id="isPrivate"
              label={t('privateLabel')}
            />
            {isPrivateValue && (
              <>
                <Text text={t('privateWarning')} font="regular14" />
                <Form.SelectMulti
                  label={t('membersLabel')}
                  defaultValue={selectedMembers}
                  //@ts-ignore
                  id="members"
                  options={memberOptions}
                />
              </>
            )}
          </Stack>
        </Sidebar.SplitContainer>
      </Stack>
      <Sidebar.Footer>
        <Form.Actions>
          <Form.Reset onClick={hideSidebar} text={t('core.cancel')} variant="secondary" />
          <Form.Submit
            text={workspaceId ? t('core.save') : t('createSpace')}
            variant="primary-blue"
            isDisabled={!isDirty || updateMutation.isLoading || createMutation.isLoading}
          />
        </Form.Actions>
      </Sidebar.Footer>
    </Form>
  );
};

const locale = {
  en: {
    createWorkspace: 'Create new Workspace',
    editWorkspace: 'Edit Workspace',
    details: 'Details',
    detailsDescription:
      "Create a new space to manage, organise and control access to different journeys. Each space has it's own Journeys, Forms and Documents.",
    title: 'Title',
    titlePlaceholder: 'e.g. Sales, Marketing, HR',
    description: 'Description',
    descriptionPlaceholder: 'Optional details about the workspace',
    createSpace: 'Create space',
    chooseIconAndColor: 'Icon',
    privacy: 'Workspace privacy',
    privateDescription:
      "Private workspaces are visible only to members of the workspace. Public workspaces are visible to all users in the company. You will be included in the members by default and can't remove yourself.",
    privateLabel: 'Private',
    privateWarning: 'Added members will have access to all data within this workspace.',
    membersLabel: 'Members',
    iconChooser: 'Icon chooser',
  },
};
